import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { injectQuery, injectMutation, injectQueryClient } from '@ngneat/query'
import { Organization } from './organizations.service'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { AgendaItemSubCategory } from './calls.service'
import { CallProfileToBeCreated } from './support/call-profiles/support-call-profiles-new-page/support-call-profiles-new-page.component'

export type AgendaItemExample = {
  content: string
  subCategory: AgendaItemSubCategory
}

export type CallProfile = {
  id: number
  name: string
  organizationId: number
  agendaItemExamples: Partial<Record<AgendaItemSubCategory, AgendaItemExample[]>>
  templateKey: string
}

export type CallProfileWithoutId = Omit<CallProfile, 'id'>

export type CallProfileWithOrganization = CallProfile & {
  organization: Organization
}

export type CallProfileOption = Pick<CallProfile, 'id' | 'name'>

@Injectable({
  providedIn: 'root',
})
export class CallProfilesService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)

  getAllCallProfiles() {
    return this.query({
      queryKey: queryKeys.callProfiles.all,
      queryFn: () => this.httpClient.get<CallProfileWithOrganization[]>('/api/call-profiles'),
    }).result$
  }

  getOwnCallProfiles() {
    return this.query({
      queryKey: queryKeys.callProfiles.own,
      queryFn: () => this.httpClient.get<CallProfileOption[]>('/api/call-profiles/own'),
    }).result$
  }

  getCallProfilesByOrganizationId(organizationId: number) {
    return this.query({
      queryKey: queryKeys.callProfiles.all,
      queryFn: () => this.httpClient.get<CallProfileWithOrganization[]>(`/api/call-profiles?organizationId=${organizationId}`),
    }).result$
  }

  getCallProfileById(id: number) {
    return this.query({
      queryKey: queryKeys.callProfiles.byId(id),
      queryFn: () => this.httpClient.get<CallProfileWithOrganization>(`/api/call-profiles/${id}`),
    }).result$
  }

  updateCallProfile() {
    return this.mutation({
      mutationFn: (callProfile: CallProfile) => this.httpClient.put<CallProfile>(`/api/call-profiles/${callProfile.id}`, callProfile),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.callProfiles.all })
        this.toastService.show('CALL_PROFILES.UPDATED')
      },
      onError: (error) => {
        this.toastService.show('CALL_PROFILES.UPDATE_FAILED')
      },
    })
  }
  createCallProfile() {
    return this.mutation({
      mutationFn: (callProfile: CallProfileToBeCreated) => this.httpClient.post<CallProfile>('/api/call-profiles', callProfile),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.callProfiles.all })
        this.toastService.show('CALL_PROFILES.CREATED')
      },
      onError: (error) => {
        this.toastService.show('CALL_PROFILES.CREATE_FAILED')
      },
    })
  }

  deleteCallProfile() {
    return this.mutation({
      mutationFn: (callProfileId: number) => this.httpClient.delete(`/api/call-profiles/${callProfileId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.callProfiles.all })
        this.toastService.show('CALL_PROFILES.DELETED')
      },
      onError: (error) => {
        this.toastService.show('CALL_PROFILES.DELETE_FAILED')
      },
    })
  }
}
